import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@material-ui/core'
import WarningIcon from '@material-ui/icons/Warning'
import { KeyboardDatePicker } from '@material-ui/pickers'
import NumberFormat from 'react-number-format'
import { DateTime } from 'luxon'
import { axiosInstance } from '../../../../helpers'
import { ListVendor, ezScheduleDetails } from '../../../../models'
import { useTimezone } from '../../../../contexts/timezone'
import {
    DayOffMode,
    DuplicateFromParams,
    DuplicateFromResponse,
    DuplicatableSchedule,
} from '../../../../hooks/useEzSchedule/types'
import { useDuplicateTurnWizardStyles } from './styles'

type WizardStep = 'source' | 'configure' | 'substitutions'

interface PhaseDateState {
    source_phase_id: number
    work_start_date: DateTime | null
    work_end_date: DateTime | null
    transfer_start_date: DateTime | null
    transfer_end_date: DateTime | null
    dest_unit_ready_date: DateTime | null
    dt_unit_ready_date: DateTime | null
}

interface Props {
    targetSchedule: ezScheduleDetails
    duplicatableSchedules: DuplicatableSchedule[]
    targetHasSetup: boolean
    loading: boolean
    vendorList: ListVendor[]
    submitting: boolean
    onGoBack: () => void
    onSubmit: (params: DuplicateFromParams) => Promise<DuplicateFromResponse>
    onComplete: (result: DuplicateFromResponse) => void
}

const dayCount = (
    start: DateTime | null,
    end: DateTime | null,
): number | null => {
    if (!start || !end) return null
    const days =
        Math.floor(end.startOf('day').diff(start.startOf('day'), 'days').days) +
        1
    return days > 0 ? days : null
}

export const DuplicateTurnWizard = (props: Props) => {
    const {
        targetSchedule,
        duplicatableSchedules,
        targetHasSetup,
        loading,
        vendorList,
        submitting,
        onGoBack,
        onSubmit,
        onComplete,
    } = props
    const classes = useDuplicateTurnWizardStyles()
    const { currentTimezone } = useTimezone()

    const [step, setStep] = useState<WizardStep>('source')
    const [
        selectedSource,
        setSelectedSource,
    ] = useState<DuplicatableSchedule | null>(null)
    const [
        sourceDetails,
        setSourceDetails,
    ] = useState<ezScheduleDetails | null>(null)
    const [sourceDetailsLoading, setSourceDetailsLoading] = useState(false)
    const [phaseDates, setPhaseDates] = useState<PhaseDateState[]>([])
    const [dayOffMode, setDayOffMode] = useState<DayOffMode>('none')
    const [vendorSubstitutions, setVendorSubstitutions] = useState<
        Record<number, number>
    >({})
    const [serviceBudgets, setServiceBudgets] = useState<
        Record<number, string>
    >({})
    const [vendorBudgets, setVendorBudgets] = useState<Record<number, string>>(
        {},
    )
    const [error, setError] = useState<string | null>(null)
    const [overwriteOpen, setOverwriteOpen] = useState(false)

    const scheduleStart = useMemo(
        () =>
            DateTime.fromISO(targetSchedule.start_date)
                .setZone(currentTimezone)
                .startOf('day'),
        [targetSchedule.start_date, currentTimezone],
    )
    const scheduleEnd = useMemo(
        () =>
            DateTime.fromISO(targetSchedule.end_date)
                .setZone(currentTimezone)
                .startOf('day'),
        [targetSchedule.end_date, currentTimezone],
    )

    useEffect(() => {
        // Always reset derived state when the source changes so a failed
        // fetch cannot leave the previous source's phases/budgets behind.
        setSourceDetails(null)
        setPhaseDates([])
        setServiceBudgets({})
        setVendorBudgets({})
        setVendorSubstitutions({})
        setDayOffMode('none')
        setError(null)

        if (!selectedSource) {
            setSourceDetailsLoading(false)
            return
        }

        let cancelled = false
        setSourceDetailsLoading(true)
        axiosInstance
            .get<ezScheduleDetails>(
                `/workorder/ez-schedule/${selectedSource.id}/`,
            )
            .then((res) => {
                if (cancelled) return
                setSourceDetails(res.data)

                const budgets: Record<number, string> = {}
                const vBudgets: Record<number, string> = {}
                res.data.phases.forEach((phase) => {
                    phase.template_services.forEach((ts) => {
                        budgets[ts.service.id] = String(ts.budget ?? 0)
                    })
                    phase.template_vendors.forEach((tv) => {
                        vBudgets[tv.vendor.id] = String(tv.budget ?? 0)
                    })
                })
                setServiceBudgets(budgets)
                setVendorBudgets(vBudgets)

                setPhaseDates(
                    selectedSource.phases.map((phase) => ({
                        source_phase_id: phase.id,
                        work_start_date: null,
                        work_end_date: null,
                        transfer_start_date: null,
                        transfer_end_date: null,
                        dest_unit_ready_date: null,
                        dt_unit_ready_date: null,
                    })),
                )

                if (selectedSource.day_off_pattern === 'sundays') {
                    setDayOffMode('sundays')
                } else if (selectedSource.day_off_pattern === 'weekends') {
                    setDayOffMode('weekends')
                } else {
                    setDayOffMode('none')
                }

                const subs: Record<number, number> = {}
                selectedSource.stale_vendors.forEach((v) => {
                    subs[v.vendor_id] = 0
                })
                setVendorSubstitutions(subs)
            })
            .catch(() => {
                if (!cancelled) {
                    setSourceDetails(null)
                    setPhaseDates([])
                    setServiceBudgets({})
                    setVendorBudgets({})
                    setVendorSubstitutions({})
                    setError('Failed to load source schedule details.')
                }
            })
            .finally(() => {
                if (!cancelled) setSourceDetailsLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [selectedSource])

    const showDayOffOptions =
        selectedSource?.day_off_pattern === 'sundays' ||
        selectedSource?.day_off_pattern === 'weekends'

    const totalSteps =
        selectedSource && selectedSource.stale_vendors.length > 0 ? 3 : 2

    const sourceDetailsReady =
        selectedSource != null &&
        sourceDetails != null &&
        sourceDetails.id === selectedSource.id &&
        phaseDates.length > 0

    type PhaseDateFieldErrors = {
        workStart?: string
        workEnd?: string
        transferStart?: string
        transferEnd?: string
        destUnitReady?: string
        dtUnitReady?: string
    }

    const phaseDateValidationErrors = useMemo(() => {
        const byPhaseId: Record<number, PhaseDateFieldErrors> = {}

        phaseDates.forEach((phase) => {
            const errors: PhaseDateFieldErrors = {}
            const workStart = phase.work_start_date?.startOf('day') ?? null
            const workEnd = phase.work_end_date?.startOf('day') ?? null
            const transferStart =
                phase.transfer_start_date?.startOf('day') ?? null
            const transferEnd = phase.transfer_end_date?.startOf('day') ?? null
            const destUnitReady =
                phase.dest_unit_ready_date?.startOf('day') ?? null
            const dtUnitReady = phase.dt_unit_ready_date?.startOf('day') ?? null

            // Mirror AutoSchedule validation
            if (workStart && workStart < scheduleStart) {
                errors.workStart = 'Must be on or after schedule start date'
            }
            if (workStart && workStart >= scheduleEnd) {
                errors.workStart = 'Must be before schedule end date'
            }

            if (workEnd && workStart && workEnd <= workStart) {
                errors.workEnd = 'Must be after work start date'
            }
            if (workEnd && workEnd > scheduleEnd) {
                errors.workEnd = 'Must be on or before schedule end date'
            }

            if (transferStart && workStart && transferStart < workStart) {
                errors.transferStart = 'Must be on or after work start date'
            }
            if (transferStart && workEnd && transferStart >= workEnd) {
                errors.transferStart = 'Must be before work end date'
            }

            if (transferEnd && transferStart && transferEnd <= transferStart) {
                errors.transferEnd = 'Must be after transfer start date'
            }
            if (transferEnd && workEnd && transferEnd >= workEnd) {
                errors.transferEnd = 'Must be before work end date'
            }

            if (destUnitReady && workStart && destUnitReady < workStart) {
                errors.destUnitReady = 'Must be on or after work start date'
            }
            if (destUnitReady && workEnd && destUnitReady > workEnd) {
                errors.destUnitReady = 'Must be on or before work end date'
            }
            if (dtUnitReady && workStart && dtUnitReady < workStart) {
                errors.dtUnitReady = 'Must be on or after work start date'
            }
            if (dtUnitReady && destUnitReady && dtUnitReady <= destUnitReady) {
                errors.dtUnitReady =
                    'Must be after the destination unit ready date'
            }
            if (dtUnitReady && workEnd && dtUnitReady > workEnd) {
                errors.dtUnitReady = 'Must be on or before work end date'
            }

            byPhaseId[phase.source_phase_id] = errors
        })

        return byPhaseId
    }, [phaseDates, scheduleStart, scheduleEnd])

    const hasDateValidationErrors = useMemo(() => {
        if (phaseDates.length === 0) {
            return true
        }
        return phaseDates.some((phase) => {
            if (!phase.work_start_date || !phase.work_end_date) {
                return true
            }
            const fieldErrors =
                phaseDateValidationErrors[phase.source_phase_id] ?? {}
            return Object.keys(fieldErrors).length > 0
        })
    }, [phaseDates, phaseDateValidationErrors])

    const substitutionsComplete = useMemo(() => {
        if (!selectedSource || selectedSource.stale_vendors.length === 0) {
            return true
        }
        return selectedSource.stale_vendors.every(
            (v) => vendorSubstitutions[v.vendor_id] > 0,
        )
    }, [selectedSource, vendorSubstitutions])

    const replacementVendorsFor = (serviceIds: number[]) => {
        return vendorList.filter((vendor) =>
            serviceIds.every((serviceId) =>
                vendor.services.some((s) => s.id === serviceId),
            ),
        )
    }

    const updatePhaseDate = (
        sourcePhaseId: number,
        patch: Partial<PhaseDateState>,
    ) => {
        setPhaseDates((prev) =>
            prev.map((phase) =>
                phase.source_phase_id === sourcePhaseId
                    ? { ...phase, ...patch }
                    : phase,
            ),
        )
    }

    const toPickerValue = (dt: DateTime | null) =>
        dt?.setZone('local', { keepLocalTime: true }) ?? null

    const fromPickerDate = (date: Date | null): DateTime | null => {
        if (date === null) return null
        return DateTime.fromJSDate(date)
            .setZone(currentTimezone, { keepLocalTime: true })
            .startOf('day')
    }

    const buildPayload = (overwrite: boolean): DuplicateFromParams => {
        if (!selectedSource) {
            throw new Error('No source selected')
        }
        if (!sourceDetailsReady) {
            throw new Error('Source schedule details are not loaded')
        }
        const subs: Record<number, number> = {}
        Object.entries(vendorSubstitutions).forEach(([fromId, toId]) => {
            if (toId > 0) {
                subs[Number(fromId)] = toId
            }
        })

        // Backend applies vendor_budgets by target vendor id. Remap through
        // substitutions; when A→B and B already has a budget, keep B's value.
        const remappedVendorBudgets: Record<number, string> = {}
        Object.entries(vendorBudgets).forEach(([fromIdStr, budget]) => {
            const fromId = Number(fromIdStr)
            const toId = subs[fromId] ?? fromId
            if (remappedVendorBudgets[toId] === undefined || fromId === toId) {
                remappedVendorBudgets[toId] = budget
            }
        })

        return {
            source_schedule_id: selectedSource.id,
            overwrite,
            day_off_mode: showDayOffOptions ? dayOffMode : 'none',
            phases: phaseDates.map((phase) => ({
                source_phase_id: phase.source_phase_id,
                work_start_date: phase.work_start_date!.toISO()!,
                work_end_date: phase.work_end_date!.toISO()!,
                transfer_start_date: phase.transfer_start_date?.toISO() ?? null,
                transfer_end_date: phase.transfer_end_date?.toISO() ?? null,
                dest_unit_ready_date:
                    phase.dest_unit_ready_date?.toISO() ?? null,
                dt_unit_ready_date: phase.dt_unit_ready_date?.toISO() ?? null,
            })),
            vendor_substitutions: subs,
            service_budgets: serviceBudgets,
            vendor_budgets: remappedVendorBudgets,
        }
    }

    const runDuplicate = async (overwrite: boolean) => {
        setError(null)
        try {
            const result = await onSubmit(buildPayload(overwrite))
            onComplete(result)
        } catch (err: any) {
            const message =
                err?.response?.data?.error ||
                err?.message ||
                'Failed to duplicate schedule template.'
            setError(message)
            setOverwriteOpen(false)
        }
    }

    const handleCreateClick = () => {
        if (hasDateValidationErrors) {
            setError('Fix the highlighted date errors before continuing.')
            return
        }
        if (!substitutionsComplete) {
            setError('Choose a replacement for every missing vendor.')
            return
        }
        if (targetHasSetup) {
            setOverwriteOpen(true)
            return
        }
        void runDuplicate(false)
    }

    const renderSourceStep = () => (
        <>
            <Typography className={classes.stepLabel}>
                Step 1 of {totalSteps}
            </Typography>
            <Typography className={classes.title}>
                Choose a previous turn
            </Typography>
            <Typography className={classes.subtitle}>
                Select a prior auto-scheduler turn to copy units, services,
                vendors, and settings from.
            </Typography>
            {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <div className={classes.sourceList}>
                    {duplicatableSchedules.map((schedule) => {
                        const selected = selectedSource?.id === schedule.id
                        const start = DateTime.fromISO(schedule.start_date)
                            .setZone(currentTimezone)
                            .toFormat('MMM d, yyyy')
                        const end = DateTime.fromISO(schedule.end_date)
                            .setZone(currentTimezone)
                            .toFormat('MMM d, yyyy')
                        return (
                            <Box
                                key={schedule.id}
                                className={`${classes.sourceRow} ${
                                    selected ? classes.sourceRowSelected : ''
                                }`}
                                onClick={() => setSelectedSource(schedule)}
                            >
                                <Typography className={classes.sourceName}>
                                    {schedule.name}
                                </Typography>
                                <Typography className={classes.sourceMeta}>
                                    {start} – {end} ·{' '}
                                    {schedule.included_unit_count} units
                                    {schedule.use_multi_phase
                                        ? ` · ${schedule.phase_count} phases`
                                        : ''}
                                    {schedule.stale_vendors.length > 0
                                        ? ` · ${schedule.stale_vendors.length} vendor(s) need replacement`
                                        : ''}
                                </Typography>
                            </Box>
                        )
                    })}
                </div>
            )}
            <div className={classes.actions}>
                <Button
                    className={classes.secondaryButton}
                    onClick={onGoBack}
                    variant="outlined"
                >
                    Cancel
                </Button>
                <Button
                    className={classes.primaryButton}
                    color="primary"
                    variant="contained"
                    disabled={!sourceDetailsReady || sourceDetailsLoading}
                    onClick={() => setStep('configure')}
                >
                    {sourceDetailsLoading ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        'Continue'
                    )}
                </Button>
            </div>
        </>
    )

    const renderConfigureStep = () => {
        if (!selectedSource || !sourceDetails) return null
        return (
            <>
                <Typography className={classes.stepLabel}>
                    Step 2 of {totalSteps}
                </Typography>
                <Typography className={classes.title}>Set Dates</Typography>
                <Typography className={classes.subtitle}>
                    Service and vendor windows will keep the same offsets as{' '}
                    {selectedSource.name}.
                </Typography>

                {phaseDates.map((phaseState, index) => {
                    const sourcePhase = selectedSource.phases.find(
                        (p) => p.id === phaseState.source_phase_id,
                    )
                    const fieldErrors =
                        phaseDateValidationErrors[phaseState.source_phase_id] ??
                        {}
                    const newDays = dayCount(
                        phaseState.work_start_date,
                        phaseState.work_end_date,
                    )
                    const oldDays = sourcePhase?.work_day_count ?? null
                    const shorter =
                        newDays != null && oldDays != null && newDays < oldDays
                    const dayCountParts = [
                        oldDays != null
                            ? `Last turn was ${oldDays} day${
                                  oldDays === 1 ? '' : 's'
                              }`
                            : 'Last turn length unknown',
                        newDays != null
                            ? `This turn is ${newDays} day${
                                  newDays === 1 ? '' : 's'
                              }`
                            : 'Select dates to see this turn length',
                    ]
                    if (shorter) {
                        dayCountParts.push(
                            'This window is shorter than last year',
                        )
                    }
                    const dayCountText = `${dayCountParts.join('. ')}.`

                    return (
                        <div
                            key={phaseState.source_phase_id}
                            className={classes.phaseBlock}
                        >
                            <div className={classes.phaseTitleRow}>
                                <Typography className={classes.phaseTitle}>
                                    {selectedSource.use_multi_phase
                                        ? `Phase ${
                                              sourcePhase?.phase_index ??
                                              index + 1
                                          }`
                                        : 'Work Window'}
                                </Typography>
                                <Typography
                                    className={`${classes.dayCountHint} ${
                                        shorter ? classes.dayCountWarning : ''
                                    }`}
                                >
                                    {dayCountText}
                                </Typography>
                            </div>
                            <div className={classes.dateRow}>
                                <KeyboardDatePicker
                                    label="Work Start"
                                    format="MM/dd/yyyy"
                                    value={toPickerValue(
                                        phaseState.work_start_date,
                                    )}
                                    onChange={(date) =>
                                        updatePhaseDate(
                                            phaseState.source_phase_id,
                                            {
                                                work_start_date: fromPickerDate(
                                                    date,
                                                ),
                                            },
                                        )
                                    }
                                    inputVariant="outlined"
                                    size="small"
                                    fullWidth
                                    error={!!fieldErrors.workStart}
                                    helperText={fieldErrors.workStart}
                                />
                                <KeyboardDatePicker
                                    label="Work End"
                                    format="MM/dd/yyyy"
                                    value={toPickerValue(
                                        phaseState.work_end_date,
                                    )}
                                    onChange={(date) =>
                                        updatePhaseDate(
                                            phaseState.source_phase_id,
                                            {
                                                work_end_date: fromPickerDate(
                                                    date,
                                                ),
                                            },
                                        )
                                    }
                                    inputVariant="outlined"
                                    size="small"
                                    fullWidth
                                    error={!!fieldErrors.workEnd}
                                    helperText={fieldErrors.workEnd}
                                />
                                <KeyboardDatePicker
                                    label="Transfer Start (Optional)"
                                    format="MM/dd/yyyy"
                                    value={toPickerValue(
                                        phaseState.transfer_start_date,
                                    )}
                                    onChange={(date) =>
                                        updatePhaseDate(
                                            phaseState.source_phase_id,
                                            {
                                                transfer_start_date: fromPickerDate(
                                                    date,
                                                ),
                                            },
                                        )
                                    }
                                    inputVariant="outlined"
                                    size="small"
                                    fullWidth
                                    clearable
                                    error={!!fieldErrors.transferStart}
                                    helperText={fieldErrors.transferStart}
                                />
                                <KeyboardDatePicker
                                    label="Transfer End (Optional)"
                                    format="MM/dd/yyyy"
                                    value={toPickerValue(
                                        phaseState.transfer_end_date,
                                    )}
                                    onChange={(date) =>
                                        updatePhaseDate(
                                            phaseState.source_phase_id,
                                            {
                                                transfer_end_date: fromPickerDate(
                                                    date,
                                                ),
                                            },
                                        )
                                    }
                                    inputVariant="outlined"
                                    size="small"
                                    fullWidth
                                    clearable
                                    error={!!fieldErrors.transferEnd}
                                    helperText={fieldErrors.transferEnd}
                                />
                                <KeyboardDatePicker
                                    label="Destination Unit Ready (Optional)"
                                    format="MM/dd/yyyy"
                                    value={toPickerValue(
                                        phaseState.dest_unit_ready_date,
                                    )}
                                    onChange={(date) =>
                                        updatePhaseDate(
                                            phaseState.source_phase_id,
                                            {
                                                dest_unit_ready_date: fromPickerDate(
                                                    date,
                                                ),
                                            },
                                        )
                                    }
                                    inputVariant="outlined"
                                    size="small"
                                    fullWidth
                                    clearable
                                    error={!!fieldErrors.destUnitReady}
                                    helperText={fieldErrors.destUnitReady}
                                />
                                <KeyboardDatePicker
                                    label="Double Transfer Unit Ready (Optional)"
                                    format="MM/dd/yyyy"
                                    value={toPickerValue(
                                        phaseState.dt_unit_ready_date,
                                    )}
                                    onChange={(date) =>
                                        updatePhaseDate(
                                            phaseState.source_phase_id,
                                            {
                                                dt_unit_ready_date: fromPickerDate(
                                                    date,
                                                ),
                                            },
                                        )
                                    }
                                    inputVariant="outlined"
                                    size="small"
                                    fullWidth
                                    clearable
                                    error={!!fieldErrors.dtUnitReady}
                                    helperText={fieldErrors.dtUnitReady}
                                />
                            </div>
                        </div>
                    )
                })}

                {showDayOffOptions && (
                    <>
                        <Typography className={classes.sectionTitle}>
                            Days Off
                        </Typography>
                        <FormControl component="fieldset">
                            <RadioGroup
                                value={dayOffMode}
                                onChange={(e) =>
                                    setDayOffMode(e.target.value as DayOffMode)
                                }
                            >
                                <FormControlLabel
                                    value="sundays"
                                    control={<Radio color="primary" />}
                                    label="Take Sundays Off"
                                />
                                <FormControlLabel
                                    value="weekends"
                                    control={<Radio color="primary" />}
                                    label="Take Saturdays And Sundays Off"
                                />
                                <FormControlLabel
                                    value="none"
                                    control={<Radio color="primary" />}
                                    label="Set Days Off On The Review Screen"
                                />
                            </RadioGroup>
                        </FormControl>
                    </>
                )}

                {sourceDetails && (
                    <>
                        <div className={classes.budgetHeader}>
                            <Typography className={classes.sectionTitle}>
                                Budgets
                            </Typography>
                            <Button
                                className={classes.clearBudgetsButton}
                                color="primary"
                                onClick={() => {
                                    setServiceBudgets((prev) => {
                                        const cleared: Record<
                                            number,
                                            string
                                        > = {}
                                        Object.keys(prev).forEach((id) => {
                                            cleared[Number(id)] = '0'
                                        })
                                        return cleared
                                    })
                                    setVendorBudgets((prev) => {
                                        const cleared: Record<
                                            number,
                                            string
                                        > = {}
                                        Object.keys(prev).forEach((id) => {
                                            cleared[Number(id)] = '0'
                                        })
                                        return cleared
                                    })
                                }}
                            >
                                Clear
                            </Button>
                        </div>
                        {Object.entries(serviceBudgets).map(
                            ([serviceId, budget]) => {
                                const serviceName =
                                    sourceDetails.phases
                                        .flatMap((p) => p.template_services)
                                        .find(
                                            (ts) =>
                                                ts.service.id ===
                                                Number(serviceId),
                                        )?.service.name ??
                                    `Service ${serviceId}`
                                return (
                                    <div
                                        key={`svc-${serviceId}`}
                                        className={classes.budgetRow}
                                    >
                                        <Typography
                                            className={classes.budgetLabel}
                                        >
                                            {serviceName}
                                        </Typography>
                                        <NumberFormat
                                            className={classes.budgetInput}
                                            customInput={TextField}
                                            variant="outlined"
                                            thousandSeparator
                                            prefix="$"
                                            value={budget}
                                            onValueChange={(values) =>
                                                setServiceBudgets((prev) => ({
                                                    ...prev,
                                                    [Number(serviceId)]:
                                                        values.value === ''
                                                            ? '0'
                                                            : values.value,
                                                }))
                                            }
                                        />
                                    </div>
                                )
                            },
                        )}
                        {Object.entries(vendorBudgets).map(
                            ([vendorId, budget]) => {
                                const vendorName =
                                    sourceDetails.phases
                                        .flatMap((p) => p.template_vendors)
                                        .find(
                                            (tv) =>
                                                tv.vendor.id ===
                                                Number(vendorId),
                                        )?.vendor.name ?? `Vendor ${vendorId}`
                                return (
                                    <div
                                        key={`ven-${vendorId}`}
                                        className={classes.budgetRow}
                                    >
                                        <Typography
                                            className={classes.budgetLabel}
                                        >
                                            {vendorName} (vendor)
                                        </Typography>
                                        <NumberFormat
                                            className={classes.budgetInput}
                                            customInput={TextField}
                                            variant="outlined"
                                            thousandSeparator
                                            prefix="$"
                                            value={budget}
                                            onValueChange={(values) =>
                                                setVendorBudgets((prev) => ({
                                                    ...prev,
                                                    [Number(vendorId)]:
                                                        values.value === ''
                                                            ? '0'
                                                            : values.value,
                                                }))
                                            }
                                        />
                                    </div>
                                )
                            },
                        )}
                    </>
                )}

                <div className={classes.actions}>
                    <Button
                        className={classes.secondaryButton}
                        onClick={() => setStep('source')}
                        variant="outlined"
                    >
                        Back
                    </Button>
                    <Button
                        className={classes.primaryButton}
                        color="primary"
                        variant="contained"
                        disabled={hasDateValidationErrors || submitting}
                        onClick={() => {
                            if (hasDateValidationErrors) {
                                setError(
                                    'Fix the highlighted date errors before continuing.',
                                )
                                return
                            }
                            if (selectedSource.stale_vendors.length > 0) {
                                setStep('substitutions')
                                return
                            }
                            handleCreateClick()
                        }}
                    >
                        {selectedSource.stale_vendors.length > 0
                            ? 'Continue'
                            : submitting
                            ? 'Creating…'
                            : 'Create from template'}
                    </Button>
                </div>
            </>
        )
    }

    const renderSubstitutionsStep = () => {
        if (!selectedSource) return null
        return (
            <>
                <Typography className={classes.stepLabel}>
                    Step 3 of {totalSteps}
                </Typography>
                <Typography className={classes.title}>
                    Replace missing vendors
                </Typography>
                <Typography className={classes.subtitle}>
                    These vendors from last year are no longer available. Choose
                    replacements to keep their dates and unit assignments.
                </Typography>
                {selectedSource.stale_vendors.map((stale) => {
                    const options = replacementVendorsFor(stale.service_ids)
                    return (
                        <div key={stale.vendor_id} className={classes.subRow}>
                            <Typography className={classes.sourceName}>
                                {stale.vendor_name}
                            </Typography>
                            <Typography className={classes.sourceMeta}>
                                Services: {stale.service_names.join(', ')}
                                {stale.missing_from_apartment
                                    ? ' · removed from property'
                                    : ''}
                            </Typography>
                            <TextField
                                select
                                label="Replacement vendor"
                                variant="outlined"
                                size="small"
                                value={
                                    vendorSubstitutions[stale.vendor_id] || ''
                                }
                                onChange={(e) =>
                                    setVendorSubstitutions((prev) => ({
                                        ...prev,
                                        [stale.vendor_id]: Number(
                                            e.target.value,
                                        ),
                                    }))
                                }
                                fullWidth
                            >
                                <MenuItem value="" disabled>
                                    Select vendor
                                </MenuItem>
                                {options.map((vendor) => (
                                    <MenuItem key={vendor.id} value={vendor.id}>
                                        {vendor.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {options.length === 0 && (
                                <Typography className={classes.errorText}>
                                    No vendors on this property offer all
                                    required services.
                                </Typography>
                            )}
                        </div>
                    )
                })}
                <div className={classes.actions}>
                    <Button
                        className={classes.secondaryButton}
                        onClick={() => setStep('configure')}
                        variant="outlined"
                        disabled={submitting}
                    >
                        Back
                    </Button>
                    <Button
                        className={classes.primaryButton}
                        color="primary"
                        variant="contained"
                        disabled={!substitutionsComplete || submitting}
                        onClick={handleCreateClick}
                    >
                        {submitting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            'Create from template'
                        )}
                    </Button>
                </div>
            </>
        )
    }

    return (
        <div className={classes.root}>
            <div className={classes.card}>
                {error && <div className={classes.warningBanner}>{error}</div>}
                {targetHasSetup && step !== 'source' && (
                    <div
                        className={classes.overwriteWarningBanner}
                        role="alert"
                    >
                        <WarningIcon className={classes.overwriteWarningIcon} />
                        <div className={classes.overwriteWarningBody}>
                            <Typography
                                className={classes.overwriteWarningTitle}
                            >
                                Warning: existing setup will be overwritten
                            </Typography>
                            <Typography
                                className={classes.overwriteWarningText}
                            >
                                This schedule already has setup data. Creating
                                from a template will erase the current template
                                setup after you confirm.
                            </Typography>
                        </div>
                    </div>
                )}
                {step === 'source' && renderSourceStep()}
                {step === 'configure' && renderConfigureStep()}
                {step === 'substitutions' && renderSubstitutionsStep()}
            </div>

            <Dialog
                open={overwriteOpen}
                onClose={() => !submitting && setOverwriteOpen(false)}
            >
                <DialogTitle>Overwrite existing setup?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This schedule already has scheduling data. Duplicating
                        from a previous turn will erase the current template
                        setup and replace it. Are you sure you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOverwriteOpen(false)}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={submitting}
                        onClick={() => void runDuplicate(true)}
                    >
                        {submitting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            'Overwrite and continue'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
